/**
 * First-login prompt to set a password.
 *
 * Invited users (chefs, school admins, cafeteria workers, parents) land via a
 * magic link and never set a password — once the link expires they can be
 * locked out with no obvious recovery. When the signed-in user has NO durable
 * sign-in method (no password AND no Google SSO), nudge them to set one, reusing
 * the profile Security card's SetPasswordDialog. Dismissible; shown once per
 * session (and never again once a password/SSO exists).
 *
 * NEVER shown while impersonating (ORDO_ADMIN "Access school/chef view"):
 * `/api/me/password` would change the IMPERSONATED user's password, and setting
 * a credential is not part of impersonation.
 */

import { useSignInMethodsQuery } from '@/api/me'
import { useAuthStore } from '@/stores/auth'

import { SetPasswordDialog } from './settings/set-password-dialog'

export function useFirstLoginPasswordPrompt() {
  const { data, isPending } = useSignInMethodsQuery()
  const isImpersonating = useAuthStore((s) => s.user?.impersonation != null)
  const dismissed = useAuthStore((s) => s.dismissedLoginPrompts.includes('password'))
  const dismiss = useAuthStore((s) => s.dismissLoginPrompt)

  const needsPassword = !isImpersonating && data != null && !data.hasPassword && !data.googleLinked
  const open = needsPassword && !dismissed
  return { needsPassword, open, isPending, dismiss: () => dismiss('password') }
}

export function FirstLoginPasswordPrompt() {
  const { needsPassword, open, dismiss } = useFirstLoginPasswordPrompt()

  if (!needsPassword) {
    return null
  }

  return (
    <SetPasswordDialog
      open={open}
      hasPassword={false}
      onOpenChange={(next) => {
        if (!next) {
          dismiss()
        }
      }}
    />
  )
}
